import { usePathname } from 'next/navigation'
import { useEffect, useState } from 'react'

import { useUser } from '@/client/context/user'
import { WEBSITE_URL } from '@/client/services/url'

import useSummerCampBanner from '@/components/Banners/hooks/useSummerCampBanner'

import getBannerComponent from './Banners/getBannerComponent'
import type { NavbarBannerType } from './types'

const NavbarBannerBase: React.FC<{
  banners: NavbarBannerType[]
  setShowBanner: (show: boolean) => void
  showingBanner: boolean
}> = ({ banners, setShowBanner, showingBanner }) => {
  const user = useUser()
  const currentUrl = `${WEBSITE_URL}${usePathname()}`
  const summerCampBanner = useSummerCampBanner()

  const [activeBanner, setActiveBanner] = useState<React.ReactElement | null>(null)

  // Append summer camp banner. It has no data requirements and is static as it gets.
  const bannersToUse = banners.concat(summerCampBanner == null ? [] : [summerCampBanner])

  // Since banner dismissals are stored in local storage, we can only figure out which
  // banner to show after component mount.
  useEffect(() => {
    const bannerStates = bannersToUse.map(banner =>
      getBannerComponent({
        user,
        banner,
        currentUrl,
        setShowBanner,
      })
    )

    // Find the first non-null banner, if any:
    const bannerToShow = bannerStates.find(bannerComponent => bannerComponent != null) ?? null
    if (bannerToShow != null) {
      setActiveBanner(bannerToShow)
      setShowBanner(true)
    } else {
      setShowBanner(false)
      setActiveBanner(null)
    }
    // NOTE: Intentionally using a custom compiled-down id as key for banners
    // oxlint-ignore-next-line react/exhaustive-deps
  }, [bannersToUse.map(item => item.slug).join('__$$__$$__')])

  return showingBanner ? activeBanner : null
}

export default NavbarBannerBase
